import React, { useEffect } from "react";
import { useField } from "formik";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormHelperText from "@mui/material/FormHelperText";
import { Autocomplete, TextField } from "@mui/material";
import { Handle } from "pages/eligibility/type";
import OutlinedDiv from "./outlined";

type SelectProps = {
  required?: boolean;
  title: string;
  identity: string[];
  name: string;
  data: {
    options?: { label: string; value: string }
  }
}
const RadioSelectInput = (props: Handle<"FormQuestionSelect"> & SelectProps & { wrap: boolean }) : JSX.Element | null => {
  const { name } = props;
  const [field, meta] = useField(name);
  const options = props.data.options || [];
  const errorClass = meta.touched && !!meta.error ? "color-hc-error" : "";
  return (
    <OutlinedDiv
      fullWidth
      label={props.title}
      required={props.required} error={meta.touched && !!meta.error}>
      <RadioGroup row={props.wrap} {...field}>
        {options.map(({ label, value }: { label: string, value: string }) => (
          <FormControlLabel
            key={`${name}-${value}`}
            value={value} className={errorClass}
            control={<Radio className={errorClass} />} label={label}
          />))}
      </RadioGroup>
      {meta.touched && meta.error && (
        <FormHelperText className={errorClass}>
          {meta.error}
        </FormHelperText>
      )}
    </OutlinedDiv>);
};

// eslint-disable-next-line max-lines-per-function
const FormQuestionSelect = (props: Handle<"FormQuestionSelect"> & SelectProps) : React.ReactElement | null => {
  const { name } = props;
  const [field, meta, helpers] = useField(name);
  const options = props.data.options || [];
  const labelsMap = options.reduce((acc: { [x: string]: string }, { label, value }) => {
    acc[value] = label;
    return acc;
  }, {});
  const { value, onBlur } = field;
  const { setValue, setTouched } = helpers;
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);
  if (!props.data.layout) {
    return (
      <Autocomplete
        value={value === "" ? null : value}
        options={options}
        getOptionLabel={(option) => (typeof option === "string" ? labelsMap[option] : option.label)}
        onChange={(_event: any, currentValue: any, reason: any) => {
          const fakeEvent = { target: { value: currentValue?.value || "", name } } as any;
          if (reason === "blur") {
            onBlur(fakeEvent);
          } else {
            setValue(currentValue ? currentValue.value : "");
            setTimeout(() => setTouched(true), 0);
          }
        }}
        isOptionEqualToValue={(option, selectedValue) => {
          if (typeof selectedValue === "string") {
            return option.value === selectedValue;
          }
          return option.value === selectedValue?.value;
        }}
        renderInput={(params) => <TextField
          margin="dense"
          name={name}
          {...params}
          required={props.required}
          label={props.title}
          variant="outlined"
          error={meta.touched && !!meta.error}
          helperText={meta.touched && !!meta.error ? meta.error : ""}
        />}
      />);
  }
  const { type, wrap } = props.data.layout;

  return type === "radio" ? <RadioSelectInput {...props} wrap={wrap}/> : null;
};

export default FormQuestionSelect;
